import { type FormEvent, useState } from 'react'

import { useChangePassword, useStepUp } from '../api/auth'
import { ApiError } from '../api/client'
import { Modal } from './Modal'
import { PasswordField } from './PasswordField'
import { useToast } from './Toast'

const MIN_LENGTH = 12

/** Contenido sin el `<Modal>` propio — así `ProfileDialog` lo puede meter
 *  en una de sus pestañas sin anidar dos modales. `ChangePasswordDialog`
 *  de acá abajo es el mismo formulario para cuando se usa suelto.
 *
 *  No usa `useWithStepUp`/`StepUpDialog` a propósito, a diferencia del
 *  resto de las acciones sensibles: este formulario YA pide la contraseña
 *  actual como parte de su propio contenido, así que abrir un popup
 *  aparte a pedirla de nuevo (el patrón genérico) es literalmente la
 *  misma pregunta dos veces — molesto, y encima una oportunidad de que
 *  las dos respuestas no coincidan entre sí sin que se note por qué
 *  "no se guardó". Cuando hace falta re-autenticar, se manda directo el
 *  valor que ya está en el campo — sin diálogo nuevo. */
export function ChangePasswordForm({ onClose }: { onClose: () => void }) {
  const changePassword = useChangePassword()
  const stepUp = useStepUp()
  const toast = useToast()

  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  // Un solo ojo para las dos: son el mismo valor escrito dos veces, así
  // que descubrir sólo una a la vez no ayuda a comparar que coincidan.
  const [showNewPassword, setShowNewPassword] = useState(false)
  // Sólo se marca después de un submit fallido, no mientras se escribe: un
  // campo que arranca "en error" antes de tocarlo es peor que no validar.
  const [mismatch, setMismatch] = useState(false)

  async function submit() {
    const result = await changePassword.mutateAsync({
      current_password: currentPassword,
      new_password: newPassword,
    })
    toast.success(result.message)
    onClose()
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (newPassword !== confirmPassword) {
      setMismatch(true)
      return
    }
    setMismatch(false)
    try {
      await submit()
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 403) return
      // Sin step-up vigente — reintenta con la contraseña actual que ya
      // está en el campo de arriba, sin volver a pedirla en un diálogo
      // aparte. Si falla acá (contraseña incorrecta), el error de esta
      // mutation es el que se muestra abajo.
      try {
        await stepUp.mutateAsync(currentPassword)
      } catch {
        return
      }
      await submit().catch(() => {})
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <h3>Cambiar contraseña</h3>
      <p className="muted">Va a cerrar el resto de tus sesiones activas — ésta sigue andando.</p>

      {(changePassword.isError || stepUp.isError) && (
        <div className="alert" role="alert">
          {changePassword.error?.message ?? stepUp.error?.message}
        </div>
      )}

      {mismatch && (
        <div className="alert" role="alert">
          Las dos contraseñas nuevas no coinciden.
        </div>
      )}

      <PasswordField
        id="current-password"
        label="Contraseña actual"
        value={currentPassword}
        onChange={setCurrentPassword}
        autoComplete="current-password"
        required
      />

      <PasswordField
        id="new-password"
        label="Contraseña nueva"
        value={newPassword}
        onChange={setNewPassword}
        autoComplete="new-password"
        required
        minLength={MIN_LENGTH}
        hint={`Al menos ${MIN_LENGTH} caracteres.`}
        visible={showNewPassword}
        onVisibleChange={setShowNewPassword}
      />

      <PasswordField
        id="confirm-password"
        label="Confirmar contraseña nueva"
        value={confirmPassword}
        onChange={setConfirmPassword}
        autoComplete="new-password"
        required
        minLength={MIN_LENGTH}
        visible={showNewPassword}
        onVisibleChange={setShowNewPassword}
      />

      <div className="confirm-dialog__actions">
        <button className="button button--ghost" type="button" onClick={onClose}>
          Cancelar
        </button>
        <button
          className="button"
          type="submit"
          disabled={changePassword.isPending || stepUp.isPending}
        >
          {changePassword.isPending || stepUp.isPending ? 'Cambiando…' : 'Cambiar contraseña'}
        </button>
      </div>
    </form>
  )
}

export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  return (
    <Modal onClose={onClose}>
      <ChangePasswordForm onClose={onClose} />
    </Modal>
  )
}
